<template>
  <v-container fluid class="points-root py-5">
    <div class="points-hero mb-4">
      <h1 class="points-title">Точки агрегаторов</h1>
      <p class="points-subtitle mb-0">
        Выбирайте агрегатор, редактируйте точки на карте drag-and-drop и удаляйте пачками.
      </p>
    </div>

    <v-row dense>
      <v-col cols="12" md="5" lg="4">
        <v-card class="panel-card mb-3" elevation="4">
          <v-card-title class="pb-1">Источник данных</v-card-title>
          <v-card-text>
            <v-select
              v-model="selectedAggregator"
              :items="aggregators"
              item-text="name"
              item-value="code"
              label="Агрегатор"
              :loading="loadingAggregators"
              :disabled="loadingAggregators"
              outlined
              dense
              clearable
            />

            <div class="d-flex align-center mt-3">
              <v-btn
                color="primary"
                class="mr-2"
                :disabled="!selectedAggregator"
                :loading="loadingPoints"
                @click="loadPoints"
              >
                Обновить
              </v-btn>
              <v-chip small outlined color="primary" v-if="currentAggregatorName">
                {{ currentAggregatorName }}
              </v-chip>
            </div>

            <div class="mt-3 stats-row">
              <div class="stats-item">
                <span class="stats-label">Всего точек</span>
                <strong>{{ points.length }}</strong>
              </div>
              <div class="stats-item">
                <span class="stats-label">Выделено</span>
                <strong>{{ selectedPoints.length }}</strong>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-card class="panel-card mb-3" elevation="4">
          <v-card-title class="pb-1">Добавить точку</v-card-title>
          <v-card-subtitle class="pt-0">
            Адрес или координаты. Координаты можно потом двигать мышью на карте.
          </v-card-subtitle>

          <v-card-text>
            <v-form @submit.prevent="createPoint">
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.title"
                    label="Название"
                    dense
                    outlined
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.address"
                    label="Адрес"
                    dense
                    outlined
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="form.lat"
                    label="Lat"
                    dense
                    outlined
                    type="number"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="form.lon"
                    label="Lon"
                    dense
                    outlined
                    type="number"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.meta"
                    label="Meta JSON"
                    dense
                    outlined
                    placeholder='{"pvz_id":123}'
                  />
                </v-col>
              </v-row>

              <div class="d-flex justify-end">
                <v-btn
                  color="primary"
                  type="submit"
                  :disabled="!selectedAggregator || !canSubmitSingle"
                  :loading="savingSingle"
                >
                  Сохранить точку
                </v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>

        <v-card class="panel-card" elevation="4">
          <v-card-title class="pb-1">Массовая загрузка</v-card-title>
          <v-card-subtitle class="pt-0">
            Формат строки: <code>Название;Адрес;lat;lon</code>
          </v-card-subtitle>

          <v-card-text>
            <v-textarea
              v-model="bulkRaw"
              outlined
              rows="5"
              placeholder="Склад №1;Москва, ул. Пушкина, д.10;55.75;37.61"
            />

            <div class="d-flex justify-space-between align-center mt-2">
              <div class="caption grey--text text--darken-1">
                Пустые строки игнорируются.
              </div>
              <v-btn
                color="primary"
                :disabled="!selectedAggregator || !bulkRaw.trim()"
                :loading="savingBulk"
                @click="bulkCreatePoints"
              >
                Импорт
              </v-btn>
            </div>

            <div v-if="bulkResult" class="mt-3 caption">
              Успешно: <strong>{{ bulkResult.inserted_count }}</strong>,
              ошибок: <strong>{{ bulkResult.failed_count }}</strong>
            </div>
            <ul v-if="bulkResult && bulkResult.failed && bulkResult.failed.length" class="error-list mt-2">
              <li v-for="(f, idx) in bulkResult.failed.slice(0, 5)" :key="idx">
                {{ f.reason }}
              </li>
            </ul>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="7" lg="8">
        <v-card class="panel-card map-card mb-3" elevation="4">
          <v-card-title class="d-flex align-center">
            <span>Карта точек</span>
            <v-spacer />
            <v-chip x-small color="primary" outlined>
              Перетащите маркер для перемещения
            </v-chip>
          </v-card-title>

          <v-card-text>
            <div ref="pointsMap" class="points-map">
              <div v-if="mapLoading" class="map-overlay">
                Загружаем VK Maps...
              </div>
              <div v-else-if="mapError" class="map-overlay map-overlay--error">
                {{ mapError }}
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-card class="panel-card" elevation="4">
          <v-card-title class="d-flex align-center">
            <span>Точки агрегатора</span>
            <span v-if="currentAggregatorName" class="ml-2 grey--text text--darken-1">— {{ currentAggregatorName }}</span>
            <v-spacer />
            <v-btn
              color="error"
              small
              :disabled="!selectedPoints.length"
              :loading="deletingBulk"
              @click="bulkDeleteDialog = true"
            >
              Удалить выбранные ({{ selectedPoints.length }})
            </v-btn>
          </v-card-title>

          <v-data-table
            v-model="selectedPoints"
            :headers="pointHeaders"
            :items="points"
            :loading="loadingPoints"
            item-key="id"
            show-select
            dense
            class="elevation-0"
          >
            <template slot="item.lat" slot-scope="{ item }">
              {{ formatCoord(item.lat) }}
            </template>

            <template slot="item.lon" slot-scope="{ item }">
              {{ formatCoord(item.lon) }}
            </template>

            <template slot="item.meta" slot-scope="{ item }">
              <span v-if="item.meta">{{ shortMeta(item.meta) }}</span>
              <span v-else class="grey--text text--darken-1">—</span>
            </template>

            <template slot="item.actions" slot-scope="{ item }">
              <v-btn icon small title="Показать на карте" @click="focusPoint(item)">
                <v-icon small color="primary">mdi-crosshairs-gps</v-icon>
              </v-btn>
              <v-btn icon small title="Удалить точку" @click="openSingleDeleteDialog(item)">
                <v-icon small color="red">mdi-delete</v-icon>
              </v-btn>
            </template>

            <template slot="no-data">
              <div class="pa-5 grey--text text--darken-1">
                Нет точек. Выберите агрегатор и загрузите список.
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="singleDeleteDialog" max-width="420">
      <v-card>
        <v-card-title>Удалить точку?</v-card-title>
        <v-card-text>
          Точка <strong>#{{ pointToDelete ? pointToDelete.id : '' }}</strong> будет удалена без возможности восстановления.
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="singleDeleteDialog = false">Отмена</v-btn>
          <v-btn color="error" :loading="deletingSingle" @click="confirmDeletePoint">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="bulkDeleteDialog" max-width="460">
      <v-card>
        <v-card-title>Удалить выбранные точки?</v-card-title>
        <v-card-text>
          Будет удалено: <strong>{{ selectedPoints.length }}</strong>.
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="bulkDeleteDialog = false">Отмена</v-btn>
          <v-btn color="error" :loading="deletingBulk" @click="confirmBulkDelete">Удалить выбранные</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { api } from '../lib/api'
import { loadVkMaps, VK_MAPS_API_KEY } from '../lib/vkMaps'

export default {
  name: 'AdminMapPoints',
  data () {
    return {
      aggregators: [],
      selectedAggregator: null,

      loadingAggregators: false,
      loadingPoints: false,
      savingSingle: false,
      savingBulk: false,
      deletingSingle: false,
      deletingBulk: false,

      mapLoading: true,
      mapError: '',

      form: {
        title: '',
        address: '',
        lat: '',
        lon: '',
        meta: ''
      },

      bulkRaw: '',
      bulkResult: null,

      points: [],
      selectedPoints: [],

      pointToDelete: null,
      singleDeleteDialog: false,
      bulkDeleteDialog: false,

      pointHeaders: [
        { text: 'ID', value: 'id', width: 70 },
        { text: 'Название', value: 'title' },
        { text: 'Адрес', value: 'address' },
        { text: 'Lat', value: 'lat', width: 110 },
        { text: 'Lon', value: 'lon', width: 110 },
        { text: 'Meta', value: 'meta' },
        { text: 'Действия', value: 'actions', sortable: false, width: 110 }
      ],

      vk: null,
      map: null,
      pointMarkers: {}
    }
  },
  computed: {
    currentAggregatorName () {
      const ag = this.aggregators.find(a => a.code === this.selectedAggregator)
      return ag ? ag.name : ''
    },
    canSubmitSingle () {
      const hasAddress = !!this.form.address.trim()
      const hasCoords = this.form.lat !== '' && this.form.lon !== ''
      return hasAddress || hasCoords
    }
  },
  watch: {
    selectedAggregator (val) {
      this.bulkResult = null
      this.selectedPoints = []
      this.points = []
      this.clearMarkers()
      if (val) this.loadPoints()
    },
    selectedPoints () {
      this.syncMarkerSelectionStyles()
    }
  },
  created () {
    this.loadAggregators()
  },
  mounted () {
    this.initMap()
    window.addEventListener('resize', this.handleMapResize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleMapResize)
    this.clearMarkers()
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    showError (msg) {
      if (this.$toast && this.$toast.error) this.$toast.error(msg)
      else alert(msg)
    },

    showSuccess (msg) {
      if (this.$toast && this.$toast.success) this.$toast.success(msg)
    },

    async initMap () {
      this.mapLoading = true
      this.mapError = ''

      try {
        const vk = await loadVkMaps(VK_MAPS_API_KEY)
        this.vk = vk

        if (!this.$refs.pointsMap) return

        this.map = new vk.Map({
          container: this.$refs.pointsMap,
          center: [37.623082, 55.75254],
          zoom: 10,
          style: 'mmr://api/styles/main_style.json',
          hash: false
        })

        this.map.addControl(new vk.NavigationControl(), 'top-left')
        this.map.on('load', () => {
          this.mapLoading = false
          this.renderMarkers()
        })
      } catch (e) {
        console.error('initMap failed:', e)
        this.mapError = e && e.message ? e.message : 'Не удалось загрузить VK Maps'
        this.mapLoading = false
      }
    },

    handleMapResize () {
      if (this.map && this.map.resize) {
        this.map.resize()
      }
    },

    async loadAggregators () {
      this.loadingAggregators = true
      try {
        const { data } = await api.get('/aggregators')
        this.aggregators = Array.isArray(data) ? data : []
      } catch (e) {
        console.error('loadAggregators failed:', e)
        this.showError('Не удалось загрузить список агрегаторов')
      } finally {
        this.loadingAggregators = false
      }
    },

    async loadPoints () {
      if (!this.selectedAggregator) return
      this.loadingPoints = true
      try {
        const { data } = await api.get('/map-points', {
          params: { aggregator_code: this.selectedAggregator }
        })
        this.points = (data && data.points) || []
        this.selectedPoints = []

        this.$nextTick(() => {
          this.renderMarkers()
          this.fitToPoints()
          this.handleMapResize()
        })
      } catch (e) {
        console.error('loadPoints failed:', e)
        this.showError('Не удалось загрузить точки')
      } finally {
        this.loadingPoints = false
      }
    },

    resetSingleForm () {
      this.form = {
        title: '',
        address: '',
        lat: '',
        lon: '',
        meta: ''
      }
    },

    async createPoint () {
      if (!this.selectedAggregator || !this.canSubmitSingle) return

      let metaParsed = null
      if (this.form.meta && this.form.meta.trim()) {
        try {
          metaParsed = JSON.parse(this.form.meta)
        } catch (e) {
          this.showError('Meta не является корректным JSON')
          return
        }
      }

      const payload = {
        aggregator_code: this.selectedAggregator,
        title: this.form.title || null,
        address: this.form.address || ''
      }

      if (this.form.lat !== '' && this.form.lon !== '') {
        payload.lat = Number(this.form.lat)
        payload.lon = Number(this.form.lon)
      }
      if (metaParsed) payload.meta = metaParsed

      this.savingSingle = true
      try {
        await api.post('/map-points', payload)
        this.resetSingleForm()
        await this.loadPoints()
        this.showSuccess('Точка добавлена')
      } catch (e) {
        console.error('createPoint failed:', e)
        this.showError('Не удалось сохранить точку')
      } finally {
        this.savingSingle = false
      }
    },

    async bulkCreatePoints () {
      if (!this.selectedAggregator || !this.bulkRaw.trim()) return

      const lines = this.bulkRaw.split('\n')
      const validItems = []

      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue

        const parts = trimmed.split(';').map(p => p.trim())
        const [title, address, latStr, lonStr] = parts

        if (!address) continue

        const item = { title: title || null, address }
        if (latStr && lonStr && !isNaN(Number(latStr)) && !isNaN(Number(lonStr))) {
          item.lat = Number(latStr)
          item.lon = Number(lonStr)
        }
        validItems.push(item)
      }

      if (!validItems.length) {
        this.showError('Нет валидных строк для загрузки')
        return
      }

      this.savingBulk = true
      try {
        const { data } = await api.post('/map-points/bulk', {
          aggregator_code: this.selectedAggregator,
          items: validItems
        })
        this.bulkResult = data
        await this.loadPoints()
        this.showSuccess('Импорт завершён')
      } catch (e) {
        console.error('bulkCreatePoints failed:', e)
        this.showError('Не удалось выполнить массовую загрузку')
      } finally {
        this.savingBulk = false
      }
    },

    openSingleDeleteDialog (point) {
      this.pointToDelete = point
      this.singleDeleteDialog = true
    },

    async confirmDeletePoint () {
      if (!this.pointToDelete) return
      this.deletingSingle = true

      try {
        await api.delete(`/map-points/${this.pointToDelete.id}`)
        this.singleDeleteDialog = false
        this.pointToDelete = null
        await this.loadPoints()
        this.showSuccess('Точка удалена')
      } catch (e) {
        console.error('deletePoint failed:', e)
        this.showError('Не удалось удалить точку')
      } finally {
        this.deletingSingle = false
      }
    },

    async confirmBulkDelete () {
      if (!this.selectedPoints.length || !this.selectedAggregator) return
      const ids = this.selectedPoints.map(p => p.id)

      this.deletingBulk = true
      try {
        const { data } = await api.delete('/map-points/bulk', {
          data: {
            aggregator_code: this.selectedAggregator,
            ids
          }
        })

        this.bulkDeleteDialog = false
        this.selectedPoints = []
        await this.loadPoints()

        if (data && data.failed_count) {
          this.showError(`Удалено ${data.success_count}, ошибок ${data.failed_count}`)
        } else {
          this.showSuccess(`Удалено: ${data && data.success_count ? data.success_count : ids.length}`)
        }
      } catch (e) {
        console.error('bulkDeletePoints failed:', e)
        this.showError('Не удалось удалить выбранные точки')
      } finally {
        this.deletingBulk = false
      }
    },

    clearMarkers () {
      Object.keys(this.pointMarkers).forEach((id) => {
        const bundle = this.pointMarkers[id]
        if (bundle && bundle.marker) bundle.marker.remove()
      })
      this.pointMarkers = {}
    },

    renderMarkers () {
      if (!this.map || !this.vk || this.mapLoading || this.mapError) return

      this.clearMarkers()

      this.points.forEach((point) => {
        const lat = Number(point.lat)
        const lon = Number(point.lon)
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return

        const el = this.createMarkerElement(point)
        const marker = new this.vk.Marker({
          element: el,
          draggable: true
        })
          .setLngLat([lon, lat])
          .addTo(this.map)

        marker.on('dragend', () => this.handleMarkerDragEnd(point.id))

        this.pointMarkers[point.id] = { marker, el }
      })

      this.syncMarkerSelectionStyles()
    },

    createMarkerElement (point) {
      const el = document.createElement('button')
      el.type = 'button'
      el.className = 'point-marker'
      el.title = point.title || point.address || `Точка ${point.id}`

      const pulse = document.createElement('span')
      pulse.className = 'point-marker__pulse'
      const dot = document.createElement('span')
      dot.className = 'point-marker__dot'

      el.appendChild(pulse)
      el.appendChild(dot)

      el.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()

        if (event.shiftKey) {
          const targetId = Number(point.id)
          const alreadySelected = this.selectedPoints.some((p) => Number(p.id) === targetId)

          if (alreadySelected) {
            this.selectedPoints = this.selectedPoints.filter((p) => Number(p.id) !== targetId)
          } else {
            this.selectedPoints = [...this.selectedPoints, point]
          }
          return
        }

        this.focusPoint(point)
        this.selectedPoints = [point]
      })

      return el
    },

    syncMarkerSelectionStyles () {
      const selected = new Set(this.selectedPoints.map(p => Number(p.id)))
      Object.keys(this.pointMarkers).forEach((id) => {
        const bundle = this.pointMarkers[id]
        if (!bundle || !bundle.el) return

        if (selected.has(Number(id))) bundle.el.classList.add('is-selected')
        else bundle.el.classList.remove('is-selected')
      })
    },

    async handleMarkerDragEnd (pointId) {
      if (!this.selectedAggregator) return

      const bundle = this.pointMarkers[pointId]
      const pointIndex = this.points.findIndex(p => Number(p.id) === Number(pointId))
      if (!bundle || pointIndex < 0) return

      const original = {
        lat: Number(this.points[pointIndex].lat),
        lon: Number(this.points[pointIndex].lon)
      }

      const lngLat = bundle.marker.getLngLat()
      const next = {
        lat: Number(lngLat.lat),
        lon: Number(lngLat.lng)
      }

      if (next.lat === original.lat && next.lon === original.lon) return

      try {
        await api.patch(`/map-points/${pointId}`, {
          aggregator_code: this.selectedAggregator,
          lat: next.lat,
          lon: next.lon
        })

        this.$set(this.points, pointIndex, {
          ...this.points[pointIndex],
          lat: next.lat,
          lon: next.lon
        })

        this.selectedPoints = this.selectedPoints.map((p) => {
          if (Number(p.id) !== Number(pointId)) return p
          return { ...p, lat: next.lat, lon: next.lon }
        })
      } catch (e) {
        console.error('handleMarkerDragEnd failed:', e)
        bundle.marker.setLngLat([original.lon, original.lat])
        this.showError('Не удалось сохранить новое положение точки')
      }
    },

    focusPoint (point) {
      if (!this.map) return

      const lat = Number(point.lat)
      const lon = Number(point.lon)
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return

      this.map.flyTo({
        center: [lon, lat],
        zoom: 14,
        speed: 1.1,
        curve: 1.1
      })
    },

    fitToPoints () {
      if (!this.map || !this.points.length) return

      const coords = this.points
        .map(p => [Number(p.lon), Number(p.lat)])
        .filter(([lon, lat]) => Number.isFinite(lat) && Number.isFinite(lon))

      if (!coords.length) return

      if (coords.length === 1) {
        this.map.flyTo({ center: coords[0], zoom: 13 })
        return
      }

      if (this.vk && this.vk.LngLatBounds) {
        const bounds = new this.vk.LngLatBounds(coords[0], coords[0])
        coords.forEach(c => bounds.extend(c))
        this.map.fitBounds(bounds, { padding: 70, maxZoom: 14 })
        return
      }

      const avgLon = coords.reduce((acc, c) => acc + c[0], 0) / coords.length
      const avgLat = coords.reduce((acc, c) => acc + c[1], 0) / coords.length
      this.map.flyTo({ center: [avgLon, avgLat], zoom: 11 })
    },

    formatCoord (value) {
      const num = Number(value)
      return Number.isFinite(num) ? num.toFixed(6) : '—'
    },

    shortMeta (meta) {
      if (typeof meta === 'string') {
        try {
          meta = JSON.parse(meta)
        } catch (e) {
          return meta
        }
      }
      const str = JSON.stringify(meta)
      if (str.length <= 70) return str
      return str.slice(0, 67) + '...'
    }
  }
}
</script>

<style scoped>
.points-root {
  --panel-bg: linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%);
  --hero-bg: radial-gradient(circle at 0% 0%, #d6ebff 0%, #eaf6ff 35%, #f7fbff 100%);
  --ink: #1d2b45;
  --muted: #5f6f8d;
  --accent: #1f7af1;
  --accent-soft: rgba(31, 122, 241, 0.12);
  background: linear-gradient(180deg, #f4f7fb 0%, #ecf3ff 100%);
}

.points-hero {
  padding: 18px 20px;
  border-radius: 16px;
  background: var(--hero-bg);
  border: 1px solid #d8e6ff;
}

.points-title {
  margin: 0;
  font-size: 30px;
  letter-spacing: 0.3px;
  line-height: 1.1;
  color: var(--ink);
}

.points-subtitle {
  margin-top: 8px;
  color: var(--muted);
  font-size: 14px;
}

.panel-card {
  border-radius: 14px;
  border: 1px solid #dce7fc;
  background: var(--panel-bg);
}

.stats-row {
  display: flex;
  gap: 8px;
}

.stats-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 10px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #d7e2fb;
}

.stats-label {
  font-size: 11px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.map-card {
  overflow: hidden;
}

.points-map {
  position: relative;
  width: 100%;
  height: 420px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #dbe8ff;
  background: #dbe9ff;
}

.map-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(1px);
  background: rgba(245, 249, 255, 0.92);
  color: #26406b;
  font-weight: 600;
  z-index: 5;
}

.map-overlay--error {
  color: #8b1f1f;
}

code {
  background: var(--accent-soft);
  border-radius: 4px;
  padding: 2px 6px;
}

.error-list {
  margin: 0;
  padding-left: 18px;
  color: #b42323;
  font-size: 12px;
}

@media (max-width: 960px) {
  .points-title {
    font-size: 24px;
  }

  .points-map {
    height: 360px;
  }
}

@media (max-width: 600px) {
  .points-map {
    height: 320px;
  }

  .stats-row {
    flex-direction: column;
  }
}
</style>

<style>
.point-marker {
  position: relative;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: grab;
}

.point-marker:active {
  cursor: grabbing;
}

.point-marker__pulse {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(237, 63, 63, 0.22);
  animation: markerPulse 1.8s ease-out infinite;
}

.point-marker__dot {
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #f04438;
  border: 2px solid #ffffff;
  top: 6px;
  left: 6px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
}

.point-marker.is-selected .point-marker__dot {
  background: #1367d7;
}

.point-marker.is-selected .point-marker__pulse {
  background: rgba(19, 103, 215, 0.24);
}

@keyframes markerPulse {
  0% {
    transform: scale(0.8);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.55);
    opacity: 0;
  }
}
</style>
